import { Icon } from '@/components/atoms/Icon/Icon';
import { Icons } from '@/components/atoms/Icon';
import { ReferralFormType } from '@karehero/models';

// mui
import { Box, Stack, Link, Typography } from '@mui/material';

interface ReferralProcessProps {
  className?: string;
  formType?: ReferralFormType;
}

const CardWithIcon = ({
  stepLabel,
  subTitle,
  icon,
  content,
}: {
  stepLabel: string;
  subTitle: string;
  icon: Icons;
  content: string;
}) => {
  return (
    <Stack
      spacing={1}
      sx={{
        maxWidth: 250,
        justifyContent: { xs: 'center', sm: 'flex-start' },
        alignItems: { xs: 'center', sm: 'flex-start' },
      }}
    >
      <Stack
        direction='row'
        spacing={1}
        sx={{
          alignItems: 'center',
          color: 'primary.main',
        }}
      >
        <Box sx={{ borderRadius: '99999px' }}>
          <Icon icon={icon} fontSize={24} />
        </Box>
        <Typography
          variant='h2'
          sx={{
            color: 'primary.main',
          }}
        >
          {stepLabel}
        </Typography>
      </Stack>
      <Stack spacing={1}>
        <Typography
          variant='h2'
          sx={{
            fontSize: 16,
            lineHeight: 1,
            textAlign: { xs: 'center', sm: 'left' },
          }}
        >
          {subTitle}
        </Typography>
        <Typography
          variant='caption'
          sx={{ textAlign: { xs: 'center', sm: 'left' } }}
        >
          {content}
        </Typography>
      </Stack>
    </Stack>
  );
};

const SjpReferralProcess = ({ className }: ReferralProcessProps) => {
  return (
    <Stack
      sx={{
        gap: 2,
      }}
    >
      <Stack
        sx={{
          gap: 1,
        }}
      >
        <Typography
          variant='h2'
          sx={{ textAlign: { xs: 'center', sm: 'left' } }}
        >
          How your referral works
        </Typography>
        <Typography
          variant='caption'
          sx={{ textAlign: { xs: 'center', sm: 'left' } }}
        >
          See Frequently Asked Questions from other SJP Partners{' '}
          <Link
            href='/docs/sjp/KareHero-FAQ.pdf'
            target='_blank'
            rel='noopener noreferrer'
          >
            here
          </Link>
          . Please contact us at sjp@karehero.com for further questions.
        </Typography>
      </Stack>
      <Stack
        className={className}
        direction={{ xs: 'column', sm: 'row' }}
        sx={{
          alignItems: { xs: 'center', sm: 'flex-start' },
          justifyContent: 'space-around',
          gap: 2,
          width: '100%',
        }}
      >
        <CardWithIcon
          icon='memo'
          stepLabel='Step 1'
          subTitle='Create your account'
          content='Create an account with your professional email to get started.'
        />
        <CardWithIcon
          icon='envelope'
          stepLabel='Step 2'
          subTitle='Create Partner Portal'
          content='Set up your Partner Portal to manage referred clients and invite your team.'
        />
        <CardWithIcon
          icon='check-circle'
          stepLabel='Step 3'
          subTitle='Refer your clients'
          content='Submit referrals and track progress via your Partner Portal.'
        />
      </Stack>
    </Stack>
  );
};

const IfaReferralProcess = ({ className }: ReferralProcessProps) => {
  return (
    <Stack
      className={className}
      direction={{ xs: 'column', sm: 'row' }}
      sx={{
        alignItems: { xs: 'center', sm: 'flex-start' },
        justifyContent: 'space-around',
        width: '100%',
      }}
    >
      <CardWithIcon
        icon='memo'
        stepLabel='Step 1'
        subTitle='Refer your client'
        content='Invite your client to KareHero by completing the form below and agreeing to the payment terms.'
      />
      <CardWithIcon
        icon='envelope'
        stepLabel='Step 2'
        subTitle='Client receives invite'
        content='Your client receives a personalised email invite with full access to KareHero and dedicated 1:1 support.'
      />
      <CardWithIcon
        icon='check-circle'
        stepLabel='Step 3'
        subTitle='Track & Monitor'
        content="Track your client's registration and progress via your Portal homepage."
      />
    </Stack>
  );
};

/**
 * ReferralProcess describes the steps for a partner making a referral
 */
export const ReferralProcess = ({
  formType,
  ...props
}: ReferralProcessProps) => {
  return formType === ReferralFormType.SjpIFA ? (
    <SjpReferralProcess {...props} />
  ) : (
    <IfaReferralProcess {...props} />
  );
};

ReferralProcess.displayName = 'ReferralProcess';

export default ReferralProcess;
